import React from 'react';

import { useParams } from 'react-router-dom';

import { IParams } from '@domain/interfaces/IParams';
import { IOrder } from '@domain/interfaces/common/orders/IOrders';
import { IOtherGatewayProvider } from '@domain/interfaces/dashboard/Feed/IOtherGateway';

import { useToast } from '@helpers/hooks/useToast';
import { useDate } from '@helpers/hooks/useDate';
import { useStoreConfig } from '@helpers/hooks/useStoreConfig';
import { useConfig } from '@helpers/hooks/useConfig';

import ordersService from '@services/pages/dashboard/orders/orders';
import feedService from '@services/pages/dashboard/feed/feed';

const OtherGatewayContext = React.createContext<IOtherGatewayProvider | null>(null);

export const OtherGatewayProvider: React.FC = ({ children }) => {
  const { toast } = useToast();
  const { storeAliasId } = useParams<IParams>();
  const { format, utcToZonedTime, subDays } = useDate();
  const { mutateFeedQuantity, mutateFeedGateways } = useStoreConfig();
  const { analytics, user } = useConfig();

  const [isLoadingOtherGatewayOrders, setIsLoadingOtherGatewayOrders] = React.useState<boolean>(
    true,
  );
  const [otherGatewayOrders, setOtherGatewayOrders] = React.useState<Array<IOrder>>([]);
  const [otherGatewayOrdersPage, setOtherGatewayOrdersPage] = React.useState<number>(0);
  const [otherGatewayOrdersPageCount, setOtherGatewayOrdersPageCount] = React.useState<number>(1);
  const [isSolvingOrder, setIsSolvingOrder] = React.useState<boolean>(false);

  const loadOtherGatewayOrders = React.useCallback(async () => {
    setIsLoadingOtherGatewayOrders(true);

    try {
      const { data } = await feedService.listFeedOrdersPromise({
        storeAliasId,
        page: otherGatewayOrdersPage,
        startDate: format(subDays(utcToZonedTime(new Date()), 29), 'yyyy-MM-dd'),
        endDate: format(utcToZonedTime(new Date()), 'yyyy-MM-dd'),
        filter: {
          gateway_name: 'OTHER',
          status: 'NO_INFORMATION',
          type: 'NO_INFORMATION',
          order_field: 'provider_created_at',
        },
      });

      if (data) {
        setOtherGatewayOrders(currentOtherGatewayOrders => [
          ...currentOtherGatewayOrders,
          ...data.orders,
        ]);
        setOtherGatewayOrdersPageCount(data.total_pages);
      }

      setIsLoadingOtherGatewayOrders(false);
    } catch (error: any) {
      setIsLoadingOtherGatewayOrders(false);
      toast.error(error?.response?.data?.message);
    }
  }, [format, otherGatewayOrdersPage, storeAliasId, subDays, toast, utcToZonedTime]);

  const handleLoadMore = React.useCallback(() => {
    setOtherGatewayOrdersPage(oldPage => oldPage + 1);
  }, []);

  const updateOrder = React.useCallback(
    async (order: IOrder) => {
      await ordersService.updateOrder({
        storeAliasId,
        orderAliasId: order.alias_id,
        order,
      });
    },
    [storeAliasId],
  );

  const solveOtherGatewayOrder = React.useCallback(
    async (order: IOrder) => {
      setIsSolvingOrder(true);

      try {
        await updateOrder(order);

        setOtherGatewayOrders(currentOrders => {
          const filteredOrders = currentOrders.filter(
            currentOrder => currentOrder.alias_id !== order.alias_id,
          );

          return filteredOrders;
        });

        analytics?.track(
          'Task Feed Solved',
          { type: 'OTHER_GATEWAY', email: user?.email },
          { context: { groupId: storeAliasId } },
        );
        analytics?.trackHubSpot(storeAliasId, { task_feed_solved: true });

        mutateFeedQuantity();
        mutateFeedGateways();

        toast.success('Pedido atualizado com sucesso!');

        setIsSolvingOrder(false);
      } catch (error: any) {
        setIsSolvingOrder(false);
        toast.error(error?.response?.data?.message);
      }
    },
    [toast, updateOrder, mutateFeedQuantity, analytics, storeAliasId, mutateFeedGateways, user],
  );

  const hasMore = otherGatewayOrdersPage + 1 < otherGatewayOrdersPageCount;

  return (
    <OtherGatewayContext.Provider
      value={{
        handleLoadMore,
        hasMore,
        isLoadingOtherGatewayOrders,
        isSolvingOrder,
        loadOtherGatewayOrders,
        otherGatewayOrders,
        solveOtherGatewayOrder,
      }}
    >
      {children}
    </OtherGatewayContext.Provider>
  );
};

export const useOtherGateway = (): IOtherGatewayProvider => {
  const context = React.useContext(OtherGatewayContext);

  if (!context) {
    throw new Error('useOtherGateway must be used within OtherGatewayContext');
  }

  return context;
};
